package com.arena_web.ui.components.feed

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.CreateUserPostRequest
import com.arena_web.data.dto.UserPostDto
import com.arena_web.domain.repositories.UserPostRepository
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.varabyte.kobweb.silk.style.toModifier
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arena_web.utils.CityResult
import com.arena_web.utils.searchCities
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

// Figma "Create Post" — node 7991:177892
// Six layouts:
//   1 — 1 photo (portrait)
//   2 — 1 photo (landscape)
//   3 — 2 side-by-side
//   4 — 2 stacked
//   5 — 1 wide + 2 side-by-side
//   6 — 2 side-by-side + 1 wide

private const val MAX_POST_BYTES = 50 * 1024 * 1024 // 50 MB matches backend cap

private fun slotsFor(layout: Int): Int = when (layout) {
    1 -> 1
    2 -> 1
    3 -> 2
    4 -> 2
    5 -> 3
    6 -> 3
    else -> 1
}

@Composable
fun CreatePostPopup(
    isOpen: Boolean,
    postRepository: UserPostRepository,
    onDismiss: () -> Unit,
    onCreated: (UserPostDto) -> Unit
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var layout by remember { mutableStateOf(1) }
    var slotImages by remember { mutableStateOf<List<SlotImage?>>(List(4) { null }) }
    var caption by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun setSlot(index: Int, value: SlotImage?) {
        slotImages = slotImages.mapIndexed { i, prev -> if (i == index) value else prev }
    }

    fun reset() {
        layout = 1
        slotImages = List(4) { null }
        caption = ""
        city = ""
        submitting = false
        error = null
    }

    val dismiss: () -> Unit = { reset(); onDismiss() }

    val submit: () -> Unit = submit@{
        val required = slotsFor(layout)
        val provided = slotImages.take(required).filterNotNull()
        if (provided.size < required) {
            error = t.feedAddAllPhotos(required)
            return@submit
        }
        submitting = true
        error = null
        AnalyticsTracker.trackClick("create_post", screen = "feed")
        scope.launch {
            val uploads = try {
                coroutineScope {
                    provided.map { slot ->
                        async {
                            postRepository.uploadImage(slot.bytes, slot.fileName, slot.contentType)
                        }
                    }.awaitAll()
                }
            } catch (
                @Suppress("TooGenericExceptionCaught") e: Throwable
            ) {
                error = e.message ?: t.feedUploadFailed
                submitting = false
                return@launch
            }
            val urls = uploads.mapNotNull { it.getOrNull()?.imageUrl }
            if (urls.size != provided.size) {
                error = uploads.firstNotNullOfOrNull { it.exceptionOrNull()?.message } ?: t.feedUploadFailed
                submitting = false
                return@launch
            }
            val create = postRepository.createPost(
                CreateUserPostRequest(
                    content = caption.trim(),
                    imageUrls = urls,
                    layout = layout,
                    city = city.trim().takeIf { it.isNotBlank() }
                )
            )
            val post = create.getOrNull()
            if (create.isFailure || post == null) {
                error = create.exceptionOrNull()?.message ?: t.feedCreatePostFailed
                submitting = false
                return@launch
            }
            onCreated(post)
            reset()
            onDismiss()
        }
    }

    ModalScaffold(onOverlayClick = dismiss, maxWidthPx = 1189) {
        Column(
            modifier = HiddenScrollbarStyle.toModifier()
                .fillMaxWidth()
                .sBorderRadius(22)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("background", "#09142d")
                    property("padding", "${s(22)} ${s(40)} ${s(32)} ${s(24)}")
                    property("max-height", "92vh")
                    property("overflow-y", "auto")
                }
                .sGap(24)
        ) {
            // Header — title + subtitle + gradient divider (same fade as the
            // vertical divider between the two columns, rotated 90°).
            Column(
                modifier = Modifier.fillMaxWidth().sGap(16)
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth().sGap(12)
                ) {
                    SpanText(
                        t.feedCreatePostTitle,
                        modifier = Modifier
                            .sFontSize(24)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(32)
                            .sLetterSpacing(0.24)
                            .color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        t.feedChooseLayoutSubtitle,
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(AppColors.Text.OnSurfaceMuted)
                    )
                }
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                            )
                        }
                )
            }

            // Two-column body: drop zones (left) | divider | layout selector + form (right)
            // No verticalAlignment on the Row — we rely on `align-items: stretch` so the
            // left column gets the same height as the (usually taller) right column, and
            // then center the drop-zone card inside it.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(22).styleModifier {
                    property("align-items", "stretch")
                }
            ) {
                // ── LEFT column: drop zones (centered both axes via CSS grid) ─────
                // Using `display: grid; place-items: center` because Column with
                // verticalArrangement = Center was collapsing to content height,
                // leaving the card stuck at the top of the column.
                Box(
                    modifier = Modifier.styleModifier {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                        property("align-self", "stretch")
                        property("display", "grid")
                        property("place-items", "center")
                    }
                ) {
                    Box(
                        modifier = Modifier
                            .sBorderRadius(32)
                            .styleModifier {
                                property("padding", s(24))
                                property("border", "1px solid rgba(60, 121, 241, 0.45)")
                                property(
                                    "background",
                                    "linear-gradient(189deg, rgba(60, 121, 241, 0.135) 38%, rgba(5, 4, 12, 0.03) 62%)"
                                )
                            }
                    ) {
                        PostLayoutDropZones(
                            layout = layout,
                            slots = slotImages,
                            onPick = { index ->
                                pickImageFile(MAX_POST_BYTES, onTooLarge = { error = t.feedFileTooLarge }) { bytes, name, mime ->
                                    setSlot(index, SlotImage(bytes, name, mime, createObjectUrl(bytes, mime)))
                                    error = null
                                }
                            },
                            onClear = { index -> setSlot(index, null) }
                        )
                    }
                }

                // ── Vertical divider ──────────────────────────────────────────
                Box(
                    modifier = Modifier.styleModifier {
                        property("width", "1px")
                        property("align-self", "stretch")
                        property(
                            "background",
                            "linear-gradient(180deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                        )
                    }
                )

                // ── RIGHT column: layout selector + form + buttons ───────────
                Column(
                    modifier = Modifier.sWidth(394).sGap(40),
                    horizontalAlignment = Alignment.Start
                ) {
                    // Layout selector block
                    Column(
                        modifier = Modifier.fillMaxWidth().sGap(24)
                    ) {
                        SpanText(
                            t.feedChooseLayout,
                            modifier = Modifier
                                .sFontSize(18)
                                .fontWeight(AppTypography.Medium)
                                .sLineHeight(22)
                                .sLetterSpacing(0.09)
                                .color(AppColors.Text.Secondary)
                        )
                        // 3×2 grid: tiles 1-3 on first row, 4-6 on second row.
                        // Top alignment keeps the number circles on a single horizontal
                        // line across each row (portrait tiles are taller than landscape,
                        // so centering would shift the middle number down).
                        Column(
                            modifier = Modifier.fillMaxWidth().sGap(24)
                        ) {
                            listOf(listOf(1, 2, 3), listOf(4, 5, 6)).forEach { ids ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().sGap(40),
                                    horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center,
                                    verticalAlignment = Alignment.Top
                                ) {
                                    ids.forEach { id ->
                                        LayoutOptionTile(
                                            index = id,
                                            active = layout == id,
                                            onClick = {
                                                if (layout != id) {
                                                    layout = id
                                                    slotImages = slotImages.mapIndexed { i, prev ->
                                                        if (i >= slotsFor(id)) null else prev
                                                    }
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // Form fields + buttons
                    Column(
                        modifier = Modifier.fillMaxWidth().sGap(16)
                    ) {
                        PostCityField(
                            value = city,
                            onValueChange = { city = it }
                        )
                        PostContentField(
                            value = caption,
                            onValueChange = { caption = it }
                        )

                        if (error != null) {
                            SpanText(
                                error!!,
                                modifier = Modifier.sFontSize(12).color(AppColors.Error)
                            )
                        }

                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sGap(12)
                                .sPaddingTop(12),
                            horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SecondaryPillButton(t.commonCancel, onClick = dismiss)
                            PrimaryPillButton(
                                text = if (submitting) t.feedCreating else t.feedCreatePostTitle,
                                enabled = !submitting,
                                onClick = submit
                            )
                        }
                    }
                }
            }
        }
    }
}

internal data class SlotImage(
    val bytes: ByteArray,
    val fileName: String,
    val contentType: String,
    val previewUrl: String
)

// =============================================================================
// Layout selector tile — number circle on top, layout preview below
// Figma: tile 1 is portrait (narrow), tiles 2-6 are landscape (wider)
// =============================================================================
@Composable
private fun LayoutOptionTile(
    index: Int,
    active: Boolean,
    onClick: () -> Unit
) {
    // Figma: every tile shares the same 4:3 aspect ratio, just rotated. Portrait
    // tiles (1, 3) are 78 × 104 (3:4, taller than wide); landscape tiles (2, 4, 5, 6)
    // are 104 × 78 (4:3, wider than tall) — pure inverses of each other.
    // Row 2 (all landscape): 3 × 104 + 2 × 40 = 392 ≤ 394 right-column width.
    val isPortraitTile = index == 1 || index == 3
    val outerW = if (isPortraitTile) 78 else 104
    val outerH = if (isPortraitTile) 104 else 78
    // Fix the tile column to the widest variant (104). This way the number
    // circle and preview are horizontally centered inside the same slot for
    // every tile, so the digits land on the same X grid across both rows.
    val tileSlotW = 104
    val colGap = 22
    Column(
        modifier = Modifier
            .sWidth(tileSlotW)
            .sGap(colGap)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Number circle — 32×32 with gradient-mask border (same as the "+" button)
        Box(
            modifier = Modifier
                .sSize(32)
                .sBorderRadius(100)
                .styleModifier {
                    property(
                        "background",
                        AppGradients.InteractiveSecondary
                    )
                    property("position", "relative")
                },
            contentAlignment = Alignment.Center
        ) {
            // Gradient border overlay (mask-composite trick)
            Box(
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(100))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )
            SpanText(
                "$index",
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.Text.Secondary)
            )
        }
        // Layout preview frame — border via mask-composite overlay (same technique for both
        // states, only the background swaps — no layout shift). Inner mini-preview sized
        // explicitly so it centers reliably inside the frame.
        Box(
            modifier = Modifier
                .sWidth(outerW)
                .sHeight(outerH)
                .sBorderRadius(7)
                .styleModifier {
                    property("position", "relative")
                },
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(7))
                    val borderBg = if (active) "linear-gradient(#0c42ff, #0c42ff)" else AppGradients.ButtonBorderGradientPrimary
                    property("background", "$borderBg border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )
            Box(
                modifier = Modifier.sWidth(outerW - 30).sHeight(outerH - 30),
                contentAlignment = Alignment.Center
            ) {
                LayoutMiniPreview(index = index, active = active)
            }
        }
    }
}

@Composable
private fun LayoutMiniPreview(index: Int, active: Boolean) {
    // Full-cell layouts (1, 2) show a larger image-outline icon; multi-cell layouts (3-6)
    // show a smaller icon inside each cell.
    val bigIcon = 14
    val smallIcon = 9
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        when (index) {
            1 -> MiniCell(active, iconSize = bigIcon, modifier = Modifier.fillMaxSize())
            2 -> MiniCell(active, iconSize = bigIcon, modifier = Modifier.fillMaxSize())
            3 -> Row(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
            }
            4 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.fillMaxWidth().flexGrow(1))
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.fillMaxWidth().flexGrow(1))
            }
            5 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.fillMaxWidth().flexGrow(1))
                Row(modifier = Modifier.fillMaxWidth().flexGrow(1).sGap(3)) {
                    MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
                    MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
                }
            }
            6 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                Row(modifier = Modifier.fillMaxWidth().flexGrow(1).sGap(3)) {
                    MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
                    MiniCell(active, iconSize = smallIcon, modifier = Modifier.flexGrow(1).fillMaxHeight())
                }
                MiniCell(active, iconSize = smallIcon, modifier = Modifier.fillMaxWidth().flexGrow(1))
            }
        }
    }
}

@Composable
private fun MiniCell(active: Boolean, iconSize: Int, modifier: Modifier = Modifier) {
    val cellBg = if (active) "#004390" else "#0a2e67"
    Box(
        modifier = modifier
            .sBorderRadius(4)
            .styleModifier {
                property("background", cellBg)
                property("border", "0.3px solid rgba(148, 149, 152, 0.3)")
            },
        contentAlignment = Alignment.Center
    ) {
        // Recolour via mask-image so the icon shows the exact Figma colour (#506892)
        // instead of the SVG's default stroke colour.
        Box(
            modifier = Modifier
                .sSize(iconSize)
                .styleModifier {
                    property("background-color", "#506892")
                    val maskValue = "url('${AppIcons.ImageOutline}') no-repeat center / contain"
                    property("mask", maskValue)
                    property("-webkit-mask", maskValue)
                }
        )
    }
}

// =============================================================================
// Drop zones rendered per layout
// =============================================================================
@Composable
private fun PostLayoutDropZones(
    layout: Int,
    slots: List<SlotImage?>,
    onPick: (Int) -> Unit,
    onClear: (Int) -> Unit
) {
    // Figma — inner drop area is 619px wide for layouts 2-6 (split rows × 196 / 150 px),
    // and 355.5×608 portrait for layout 1.
    val wideW = 619
    val sideRowH = 196
    val splitRowH = 150
    val wideTotalH = sideRowH + 12 + splitRowH // 358
    val twoCol = Modifier.sWidth(wideW).sHeight(wideTotalH)
    when (layout) {
        1 -> DropSlot(
            slots.getOrNull(0),
            modifier = Modifier.sWidth(355).sHeight(608),
            onPick = { onPick(0) },
            onClear = { onClear(0) }
        )
        2 -> DropSlot(
            slots.getOrNull(0),
            modifier = Modifier.sWidth(wideW).sHeight(wideTotalH),
            onPick = { onPick(0) },
            onClear = { onClear(0) }
        )
        3 -> Row(modifier = twoCol.sGap(12)) {
            // flex-basis: 0 keeps the two columns at equal width regardless of inner content.
            val slotMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
            DropSlot(slots.getOrNull(0), modifier = slotMod, onPick = { onPick(0) }, onClear = { onClear(0) })
            DropSlot(slots.getOrNull(1), modifier = slotMod, onPick = { onPick(1) }, onClear = { onClear(1) })
        }
        4 -> Column(modifier = twoCol.sGap(12)) {
            // flex-basis: 0 keeps the two rows at equal height regardless of inner content.
            val slotMod = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("flex-basis", "0") }
            DropSlot(slots.getOrNull(0), modifier = slotMod, onPick = { onPick(0) }, onClear = { onClear(0) })
            DropSlot(slots.getOrNull(1), modifier = slotMod, onPick = { onPick(1) }, onClear = { onClear(1) })
        }
        5 -> Column(modifier = Modifier.sWidth(wideW).sGap(12)) {
            // Wide top slot (196 tall), then two side-by-side below (150 tall each).
            DropSlot(slots.getOrNull(0), modifier = Modifier.fillMaxWidth().sHeight(sideRowH), onPick = { onPick(0) }, onClear = { onClear(0) })
            Row(modifier = Modifier.fillMaxWidth().sHeight(splitRowH).sGap(12)) {
                val slotMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
                DropSlot(slots.getOrNull(1), modifier = slotMod, onPick = { onPick(1) }, onClear = { onClear(1) })
                DropSlot(slots.getOrNull(2), modifier = slotMod, onPick = { onPick(2) }, onClear = { onClear(2) })
            }
        }
        6 -> Column(modifier = Modifier.sWidth(wideW).sGap(12)) {
            // Two side-by-side on top (150 tall), wide slot below (196 tall).
            Row(modifier = Modifier.fillMaxWidth().sHeight(splitRowH).sGap(12)) {
                val slotMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
                DropSlot(slots.getOrNull(0), modifier = slotMod, onPick = { onPick(0) }, onClear = { onClear(0) })
                DropSlot(slots.getOrNull(1), modifier = slotMod, onPick = { onPick(1) }, onClear = { onClear(1) })
            }
            DropSlot(slots.getOrNull(2), modifier = Modifier.fillMaxWidth().sHeight(sideRowH), onPick = { onPick(2) }, onClear = { onClear(2) })
        }
    }
}

@Composable
private fun DropSlot(
    slot: SlotImage?,
    modifier: Modifier = Modifier,
    onPick: () -> Unit,
    onClear: () -> Unit
) {
    // Slot — dashed border only (outer "Selected Layout Container" provides the solid frame)
    Box(
        modifier = modifier
            .sBorderRadius(22)
            .cursor(Cursor.Pointer)
            .onClick { if (slot == null) onPick() }
            .styleModifier {
                property("box-sizing", "border-box")
                property("position", "relative")
                property("overflow", "hidden")
                property("min-width", "0")
                property("min-height", "0")
                if (slot == null) {
                    property("border", "2px dashed #0c1787")
                    property(
                        "background",
                        "linear-gradient(200deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                    )
                } else {
                    // Image rendered as a CSS background so it can never affect the
                    // slot's size — `cover` crops to fit, the slot stays exactly the
                    // dimensions dictated by the layout.
                    property("border", "2px solid #0c1787")
                    property("background-image", "url('${slot.previewUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("background-repeat", "no-repeat")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (slot == null) {
            val t = LocalAppStrings.current
            Column(
                modifier = Modifier.sGap(10),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // 48×48 circle with + icon — same gradient-mask border as the Add Story button
                Box(
                    modifier = Modifier
                        .sSize(48)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property(
                                "background",
                                AppGradients.InteractiveSecondary
                            )
                            property("position", "relative")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    // Gradient border overlay (mask-composite trick)
                    Box(
                        modifier = Modifier.fillMaxSize().styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("border", "0.3px solid transparent")
                            property("border-radius", s(100))
                            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("-webkit-mask-composite", "xor")
                            property("mask-composite", "exclude")
                            property("pointer-events", "none")
                        }
                    )
                    Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(20))
                }
                SpanText(
                    t.feedAddPhoto,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .sLetterSpacing(0.032)
                        .color(AppColors.Text.Secondary)
                )
            }
        } else {
            // Remove button (top-right) — image itself is rendered as the slot's CSS
            // background, so it can't push the slot to grow.
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(8))
                        property("right", s(8))
                        property("background", "rgba(0, 0, 0, 0.6)")
                        property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                    }
                    .onClick {
                        it.stopPropagation()
                        onClear()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "×",
                    modifier = Modifier.sFontSize(16).color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

// =============================================================================
// Optional metadata fields — pill-shaped, gradient border (matches CreateEventPopup)
// =============================================================================
@Composable
private fun PostCityField(
    value: String,
    onValueChange: (String) -> Unit
) {
    val t = LocalAppStrings.current
    var suggestions by remember { mutableStateOf<List<CityResult>>(emptyList()) }
    var showSuggestions by remember { mutableStateOf(false) }
    // Skip the next debounced search after the user picks a suggestion — otherwise
    // setting `value` to the chosen city would immediately re-open the dropdown.
    var skipNextSearch by remember { mutableStateOf(false) }

    LaunchedEffect(value) {
        if (skipNextSearch) {
            skipNextSearch = false
            return@LaunchedEffect
        }
        val q = value.trim()
        if (q.length < 2) {
            suggestions = emptyList()
            showSuggestions = false
            return@LaunchedEffect
        }
        delay(300)
        val results = searchCities(q)
        suggestions = results
        showSuggestions = results.isNotEmpty()
    }

    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        OptionalFieldLabel(t.feedCityLabel)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "10")
                }
        ) {
            // Input field
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(44)
                    .styleModifier { property("position", "relative") }
            ) {
                GradientBorderOverlay(borderRadius = 28)
                Row(
                    modifier = Modifier
                        .fillMaxSize()
                        .sGap(12)
                        .styleModifier {
                            property("padding", "0 ${s(8)} 0 ${s(12)}")
                            property("box-sizing", "border-box")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        src = AppIcons.Location,
                        modifier = Modifier.sSize(24).styleModifier { property("opacity", "0.7") }
                    )
                    Input(type = InputType.Text) {
                        value(value)
                        onInput { onValueChange(it.value) }
                        attr("placeholder", t.feedEnterCityPlaceholder)
                        style {
                            width(100.percent)
                            property("background", "transparent")
                            property("border", "none")
                            property("outline", "none")
                            property("font-size", s(16))
                            property("font-family", "inherit")
                            property("color", "#bed6f5")
                            property("padding", "0")
                        }
                    }
                }
            }

            // Suggestions dropdown — absolute, opens on top of subsequent fields.
            if (showSuggestions && suggestions.isNotEmpty()) {
                // Click-outside catcher
                Box(
                    modifier = Modifier
                        .position(Position.Fixed)
                        .top(0.px).left(0.px).right(0.px).bottom(0.px)
                        .zIndex(99)
                        .onClick { showSuggestions = false }
                )
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .position(Position.Absolute)
                        .zIndex(100)
                        .sBorderRadius(12)
                        .styleModifier {
                            property("top", "calc(100% + ${s(4)})")
                            property("left", "0")
                            property("max-height", "${s(200)}")
                            property("overflow-y", "auto")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                            property("background", "#0D1117")
                            property("border", "0.5px solid rgba(255,255,255,0.1)")
                            property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        }
                ) {
                    suggestions.forEach { result ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sPadding(10, 16)
                                .cursor(Cursor.Pointer)
                                .onClick {
                                    skipNextSearch = true
                                    onValueChange(result.city)
                                    showSuggestions = false
                                    suggestions = emptyList()
                                }
                                .styleModifier {
                                    property("border-bottom", "0.5px solid rgba(255,255,255,0.06)")
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column(modifier = Modifier.fillMaxWidth()) {
                                SpanText(
                                    result.city,
                                    modifier = Modifier
                                        .sFontSize(14)
                                        .fontWeight(AppTypography.Medium)
                                        .color(AppColors.Text.Secondary)
                                        .styleModifier {
                                            property("overflow", "hidden")
                                            property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                        }
                                )
                                if (!result.country.isNullOrBlank()) {
                                    SpanText(
                                        result.country!!,
                                        modifier = Modifier
                                            .sFontSize(12)
                                            .color(AppColors.Text.OnSurfaceMuted)
                                            .styleModifier {
                                                property("margin-top", "2px")
                                                property("overflow", "hidden")
                                                property("text-overflow", "ellipsis")
                                                property("white-space", "nowrap")
                                            }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PostContentField(
    value: String,
    onValueChange: (String) -> Unit
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        OptionalFieldLabel(t.feedPostContentLabel)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("position", "relative") }
        ) {
            GradientBorderOverlay(borderRadius = 18)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("padding", s(12))
                        property("box-sizing", "border-box")
                    }
            ) {
                TextArea {
                    value(value)
                    onInput { onValueChange(it.value) }
                    attr("placeholder", t.feedPostContentPlaceholder)
                    attr("rows", "4")
                    style {
                        width(100.percent)
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-size", s(16))
                        property("font-family", "inherit")
                        property("color", "#bed6f5")
                        property("padding", "0")
                        property("resize", "none")
                        property("height", "${s(72)}")
                    }
                }
            }
        }
    }
}

@Composable
private fun GradientBorderOverlay(borderRadius: Int) {
    // Gradient-mask border (same technique as chat search). Sits absolutely on top
    // of the field so the input/textarea below stays interactive.
    Box(
        modifier = Modifier.fillMaxSize().styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("border", "0.3px solid transparent")
            property("border-radius", s(borderRadius))
            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("-webkit-mask-composite", "xor")
            property("mask-composite", "exclude")
            property("pointer-events", "none")
        }
    )
}

@Composable
private fun OptionalFieldLabel(label: String) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(6).styleModifier { property("padding-left", s(4)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .color(AppColors.Text.Secondary)
        )
        SpanText(
            t.feedOptional,
            modifier = Modifier
                .sFontSize(12)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}
